import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import React, { ReactNode, useCallback, useEffect } from "react";
import { useEnvironment } from "../../Environment";
import { usePlayerStateDispatch } from "../../PlayerStateContext";
import {
  errorStateCanTryAgain,
  errorStateToString,
} from "../../shared/errorStates";
import { useConfPlayerClassName } from "../../shared/useConfPlayerClassName";
import { useAssetEnvironmentSafe } from "../AssetEnvironment";
import { ErrorBoundary } from "./ErrorBoundary";
import { SdtvPlayerError } from "./SdtvPlayerError";

interface ErrorViewProps {
  message: string;
  tryAgainClick?: () => void;
  loadingView?: boolean;
}

export function ErrorView({
  message,
  tryAgainClick,
  loadingView,
}: ErrorViewProps) {
  return (
    <div className="sdtv-error">
      <div className="sdtv-error-content">
        <p className="sdtv-error-message">
          {message}
          {loadingView && (
            <div className="sdtv-loading-wrapper">
              <div className="sdtv-loading" />
            </div>
          )}
        </p>
        {tryAgainClick && (
          <div className="sdtv-error-button-wrapper">
            <div className="sdtv-button" onClick={tryAgainClick}>
              Erneut versuchen
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function GlobalErrorBoundaryView({
  error,
  clearError,
}: {
  error: Error;
  info: React.ErrorInfo;
  clearError: () => void;
}) {
  const assetEnv = useAssetEnvironmentSafe();
  const { reset } = useQueryErrorResetBoundary();
  const dispatch = usePlayerStateDispatch();
  let sdtvError = error as SdtvPlayerError;
  if (!sdtvError.state) {
    sdtvError = {
      name: error.name,
      stack: error.stack,
      message: error.message,
      cause: error.cause,
      state: "default",
    };
  }
  const message = errorStateToString(sdtvError.state);
  const showTryAgain = errorStateCanTryAgain(sdtvError.state);
  const tryAgainClick = useCallback(() => {
    if (assetEnv?.assetId) {
      dispatch({ type: "asset-reset", assetId: assetEnv.assetId });
    }
    reset();
    clearError();
  }, [assetEnv?.assetId, reset, clearError, dispatch]);
  const { trackingFunc } = useEnvironment();
  useEffect(() => {
    trackingFunc(
      "Player Error",
      sdtvError.message,
      undefined,
      undefined,
      undefined
    );
  }, [sdtvError, trackingFunc]);

  const additionalClass = useConfPlayerClassName(assetEnv?.conferenceIndex);

  return (
    <React.Fragment>
      {additionalClass && (
        <div className={additionalClass}>
          <ErrorView
            message={message}
            tryAgainClick={showTryAgain ? tryAgainClick : undefined}
          />
        </div>
      )}
      {!additionalClass && (
        <ErrorView
          message={message}
          tryAgainClick={showTryAgain ? tryAgainClick : undefined}
        />
      )}
    </React.Fragment>
  );
}

interface GlobalErrorBoundaryProps {
  children: ReactNode;
}

export function GlobalErrorBoundary({ children }: GlobalErrorBoundaryProps) {
  // TODO: on Error is not called. Fallback Component mounts on every render / click! not always preventable due to big / small
  const assetEnv = useAssetEnvironmentSafe();
  const additionalClass = useConfPlayerClassName(assetEnv?.conferenceIndex);
  return (
    <React.Fragment>
      {additionalClass && (
        <div className={additionalClass}>
          <ErrorBoundary FallbackComponent={GlobalErrorBoundaryView}>
            {children}
          </ErrorBoundary>
        </div>
      )}
      {!additionalClass && (
        <ErrorBoundary FallbackComponent={GlobalErrorBoundaryView}>
          {children}
        </ErrorBoundary>
      )}
    </React.Fragment>
  );
}
